import React, { useCallback } from 'react';

import { FormControl, FormHelperText, Grid, ListItem } from '@material-ui/core';

import { LabelBlock } from '../index';
import { Slider, SliderListMarks, useStyles } from './RatingSlider.styles';

export type RatingSliderValue =
  | 'poor'
  | 'subpar'
  | 'satisfactory'
  | 'good'
  | 'outstanding';

type RatingSliderProps = {
  name: string;
  label?: string;
  setFieldValue?: (name: string, value: string) => void;
  value?: RatingSliderValue;
  required?: boolean;
  tooltip?: string;
  error?: string;
};

const ratingMarks = [
  {
    value: 0,
    fieldValue: 'poor',
    label: 'POOR',
  },
  {
    value: 25,
    fieldValue: 'subpar',
    label: 'SUBPAR',
  },
  {
    value: 50,
    fieldValue: 'satisfactory',
    label: 'SATISFACTORY',
  },
  {
    value: 75,
    fieldValue: 'good',
    label: 'GOOD',
  },
  {
    value: 100,
    fieldValue: 'outstanding',
    label: 'OUTSTANDING',
  },
];

const mapValueToFieldValue = (value: number) =>
  ratingMarks.find((mark) => mark.value === value)?.fieldValue;

const mapFieldValueToValue = (fieldValue: string) =>
  ratingMarks.find((mark) => mark.fieldValue === fieldValue)?.value;

const RatingSlider: React.FC<RatingSliderProps> = (props) => {
  const {
    name,
    label,
    value: fieldValue,
    required,
    tooltip,
    error,
    setFieldValue,
  } = props;

  const { input, sliderContainer } = useStyles();

  const handleClickMarkLabel = useCallback(
    (newFieldValue) => setFieldValue?.(name, newFieldValue),
    [name, setFieldValue],
  );

  return (
    <FormControl fullWidth className={input}>
      <LabelBlock
        label={label}
        required={required}
        tooltip={tooltip}
        error={error}
      />
      <Grid item className={sliderContainer}>
        <SliderListMarks>
          {ratingMarks.map(({ fieldValue, label }) => (
            <ListItem
              key={fieldValue}
              button
              onClick={() => handleClickMarkLabel(fieldValue)}
            >
              {label}
            </ListItem>
          ))}
        </SliderListMarks>
        <Slider
          name={name}
          value={mapFieldValueToValue(fieldValue || 'poor')}
          onChange={(_, newValue) => {
            const newFieldValue = mapValueToFieldValue(newValue as number);
            newFieldValue && setFieldValue?.(name, newFieldValue);
          }}
          step={null}
          marks={ratingMarks}
          defaultValue={mapFieldValueToValue('poor')}
        />
      </Grid>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </FormControl>
  );
};

export default RatingSlider;
